/**
 * Shared equipment-resolution cypher — the single definition behind every
 * surface that maps a tag reference to its owning `:Equipment`. The agent
 * runners resolve findings through the tag walk before persisting component
 * refs; UI surfaces that only hold tag paths anchor them to `:Tag` nodes
 * first and fall back to the exact-name lookup. Keeping the queries here
 * means those surfaces cannot drift apart in how an equipment link resolves.
 */

// Resolves a Tag to its physical context under the post-restructure ontology.
//
// Walk: (Tag) <-[MEASURED_BY|SET_BY]- (Observable|Setpoint)
//                                    <-[HAS_OBSERVABLE|HAS_SETPOINT]- (host)
// `host` is either an Equipment directly, or a Part nested under Equipment via
// `[:HAS_PART*0..3]`. Length 0 collapses `eq = host` when the property hangs
// off the Equipment itself, length 1+ walks up the Part chain.
//
// Equipment → Line is the spec-defined Line-[:HAS_EQUIPMENT]->Equipment edge.
// We emit only the directly-owning Line (most-specific scope); if the caller
// needs the parent line of a sub-line, it can follow HAS_SUBLINE in a
// follow-up read. Properties hosted directly on a `:Line` (a legal modeling
// for line-level KPIs like cycle time / OEE) carry no equipment; the owning
// line rides the same line columns so consumers fall back to a line ref
// instead of resolving nothing.
export const EQUIPMENT_BY_TAG_CYPHER = [
  'UNWIND $ids AS tagId',
  'MATCH (t:Tag {id: tagId})',
  'OPTIONAL MATCH (eq:Equipment)-[:HAS_PART*0..3]->(host)',
  '       -[:HAS_OBSERVABLE|HAS_SETPOINT]->(prop)',
  '       -[:MEASURED_BY|SET_BY]->(t)',
  'OPTIONAL MATCH (line:Line)-[:HAS_EQUIPMENT]->(eq)',
  'OPTIONAL MATCH (ownLine:Line)-[:HAS_OBSERVABLE|HAS_SETPOINT]->(lprop)',
  '       -[:MEASURED_BY|SET_BY]->(t)',
  'RETURN tagId AS tag_id,',
  '       t.name AS tag_name,',
  '       eq.id AS equipment_id,',
  '       eq.name AS equipment_name,',
  '       coalesce(line.id, ownLine.id) AS line_id,',
  '       coalesce(line.name, ownLine.name) AS line_name'
].join('\n')

/**
 * One row of {@link EQUIPMENT_BY_TAG_CYPHER}. Tags absent from the graph
 * produce no row (the `MATCH` drops them); a tag with no equipment context
 * carries null equipment/line fields from the `OPTIONAL MATCH`es.
 */
export interface EquipmentByTagRow {
  tag_id: string
  tag_name: string | null
  equipment_id: string | null
  equipment_name: string | null
  line_id: string | null
  line_name: string | null
}

// Exact-match fallback for references that cannot anchor a `:Tag` node —
// case-insensitive name equality only (never fuzzy: a deep link must not
// land on a lookalike asset), plus id equality for callers that already
// hold a graph id. Deterministic ORDER BY so duplicate names resolve stably.
export const EQUIPMENT_BY_NAME_CYPHER = [
  'UNWIND $names AS nm',
  'MATCH (eq:Equipment)',
  'WHERE toLower(eq.name) = toLower(nm) OR eq.id = nm',
  'WITH nm, eq ORDER BY eq.id',
  'WITH nm, collect({id: eq.id, name: eq.name}) AS matches',
  'RETURN nm AS query, matches[0] AS match, size(matches) AS matchCount'
].join('\n')

// Anchors canonical paths to their `:Tag` node through persisted `tag_path`
// only. Full graph ids bypass this query in `tagIdCandidate`.
export const TAG_ANCHOR_CYPHER = [
  'UNWIND $refs AS ref',
  'OPTIONAL MATCH (t:Tag {tag_path: ref.path})',
  'WITH ref.path AS path, t ORDER BY t.id',
  'WITH path, collect(t.id)[0] AS tagId',
  'WHERE tagId IS NOT NULL',
  'RETURN path, tagId AS tag_id'
].join('\n')

/** One row of {@link TAG_ANCHOR_CYPHER}; unmatched paths produce no row. */
export interface TagAnchorRow {
  path: string
  tag_id: string
}

/** One `$refs` entry for {@link TAG_ANCHOR_CYPHER}. */
export interface TagAnchorRef {
  path: string
}

/**
 * A tag reference classified for anchoring: full URNs are exact `:Tag` ids;
 * everything else anchors through {@link TAG_ANCHOR_CYPHER}.
 */
export type TagIdCandidate = { kind: 'id'; id: string } | { kind: 'anchor'; ref: TagAnchorRef }

/**
 * Classify one tag reference into an anchor candidate. Handles the wire
 * shapes configs carry:
 *   `urn:compute:gw/tp:default/tag:Plant%2FCAP-401%2FMode` — full id
 *   `[default]Plant/CAP-401/CapsApplied`                   — canonical path
 */
export function tagIdCandidate(ref: string): TagIdCandidate {
  const trimmed = ref.trim()
  if (trimmed.startsWith('urn:')) return { kind: 'id', id: trimmed }
  return { kind: 'anchor', ref: { path: trimmed } }
}

export interface EquipmentByNameMatch {
  id: string
  name: string | null
}

/**
 * One row of {@link EQUIPMENT_BY_NAME_CYPHER}. Names that match no
 * equipment produce no row, so `matchCount` is always >= 1 and `match` is
 * the first equipment in id order; `matchCount > 1` flags an ambiguous name.
 */
export interface EquipmentByNameRow {
  query: string
  match: EquipmentByNameMatch
  matchCount: number
}

export type ComponentByNameLabel = 'Equipment' | 'Line' | 'Process'

/**
 * Exact-match lookup for insight chips that carry a display name instead of
 * a graph id (`impacts[].process`, PM `equipment`). Same equality rules as
 * {@link EQUIPMENT_BY_NAME_CYPHER} (never fuzzy) across `:Equipment`,
 * `:Line`, and `:Process`. ORDER BY prefers the most specific label so a
 * single-match row is deterministic; `matchCount > 1` is the caller's cue
 * to leave the chip unlinked.
 */
export const COMPONENT_BY_NAME_CYPHER = [
  'UNWIND $names AS nm',
  'MATCH (n)',
  'WHERE (n:Equipment OR n:Line OR n:Process)',
  '  AND (toLower(n.name) = toLower(nm) OR n.id = nm)',
  'WITH nm, n',
  'ORDER BY CASE',
  '  WHEN n:Equipment THEN 0',
  '  WHEN n:Line THEN 1',
  '  WHEN n:Process THEN 2',
  '  ELSE 3',
  'END, n.id',
  'WITH nm, collect({',
  '  id: n.id,',
  '  name: n.name,',
  '  label: CASE',
  "    WHEN n:Equipment THEN 'Equipment'",
  "    WHEN n:Line THEN 'Line'",
  "    WHEN n:Process THEN 'Process'",
  "    ELSE 'Unknown'",
  '  END',
  '}) AS matches',
  'RETURN nm AS query, matches[0] AS match, size(matches) AS matchCount'
].join('\n')

export interface ComponentByNameMatch {
  id: string
  name: string | null
  label: ComponentByNameLabel
}

/** One row of {@link COMPONENT_BY_NAME_CYPHER}. Unmatched names produce no row. */
export interface ComponentByNameRow {
  query: string
  match: ComponentByNameMatch
  matchCount: number
}

/**
 * Unique exact match only — an ambiguous name must not become a deep link.
 */
export function pickComponentByName(row: ComponentByNameRow): ComponentByNameMatch | null {
  if (row.matchCount !== 1 || !row.match?.id) return null
  return row.match
}

/** Map a name-match label onto the chip scope the UI already understands. */
export function componentScopeForLabel(label: string): 'equipment' | 'process_line' | null {
  if (label === 'Equipment') return 'equipment'
  if (label === 'Line' || label === 'Process') return 'process_line'
  return null
}
